import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  collection, doc, serverTimestamp, Timestamp,
  writeBatch, increment, getDocs, getDoc, setDoc, query, where, orderBy, limit,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { stripeApi } from '../../firebase/functionsService';
import { buildCopyPrefill } from '../../utils/orderCopy';
import { useAuth } from '../../contexts/AuthContext';
import PaymentSetup from '../../components/orderer/PaymentSetup';
import { needsCardToPublish } from '../../utils/orderPayment';
import { isBillingProfileComplete } from '../../utils/billingProfile';
import BillingProfileNotice from '../../components/orderer/BillingProfileNotice';
import { useOrdererProjects } from '../../hooks/useOrdererProjects';
import { useOrgMembership } from '../../hooks/useOrgMembership';
import { useInvoiceFeature } from '../../hooks/useInvoiceFeature';
import { useCardFeature } from '../../hooks/useCardFeature';
import { useMyInvoiceTerms } from '../../hooks/useMyInvoiceTerms';
import { resolveInvoicePayer } from '../../utils/invoicePayer';
import PaymentMethodPicker from '../../components/orderer/PaymentMethodPicker';
import Navbar from '../../components/common/Navbar';
import AddressInput from '../../components/common/AddressInput';
import { useQuickPickups } from '../../hooks/useQuickPickups';
import {
  calculateSuggestedPrice,
  haversineKm,
} from '../../utils/priceCalculator';
import { findMassType, activeMassTypes, activeOptions, optionLabel, typeHint, optionHint, useMassCatalog, REMOVAL_MASS_TYPE } from '../../utils/massCatalog';
import { supplierAt, isOffered, offeredPlaces } from '../../utils/supplierOffers';
import { uploadPhotos } from '../../firebase/storageService';
import { orderStages, PAGES, pageComplete } from '../../utils/orderStages';
import { placesForFlow } from '../../utils/placeKinds';
import { nextHalfHourLocal, validateHalfHour } from '../../utils/halfHour';
import { priceHint as buildPriceHint, nextAutoPrice } from '../../utils/priceSuggestion';
import { resolveEquipment, toggleEquipment } from '../../utils/loadingEquipment';
import { COLORS } from '../../styles/tokens';
import { TruckLoader } from '../../components/common/truck';
import NewOrderShell, { ctaStyles } from '../../components/orderer/newOrder/NewOrderShell';
import NewOrderIcon from '../../components/orderer/newOrder/NewOrderIcon';
import RevealSection from '../../components/orderer/newOrder/RevealSection';
import MassHint from '../../components/orderer/newOrder/MassHint';
import PhotoPicker from '../../components/orderer/newOrder/PhotoPicker';
import IconField, { bareInputStyle, fieldLabelStyle } from '../../components/orderer/newOrder/IconField';
import MassTiles from '../../components/orderer/newOrder/MassTiles';
import OptionSelect from '../../components/orderer/newOrder/OptionSelect';
import EquipmentChips from '../../components/orderer/newOrder/EquipmentChips';
import QuantityField from '../../components/orderer/newOrder/QuantityField';
import SegmentedControl from '../../components/orderer/newOrder/SegmentedControl';
import SwitchRow from '../../components/orderer/newOrder/SwitchRow';
import RouteCard from '../../components/orderer/newOrder/RouteCard';
import PriceStep from '../../components/orderer/newOrder/PriceStep';
import TermsNotice, { TERMS_REQUIRED_MESSAGE } from '../../components/orderer/newOrder/TermsNotice';
import { useTerms } from '../../hooks/useTerms';
import CardPrompt from '../../components/orderer/newOrder/CardPrompt';
import TruckPicker from '../../components/orderer/newOrder/TruckPicker';
import FullLoadsPicker, { MAX_FULL_LOADS } from '../../components/orderer/newOrder/FullLoadsPicker';
import HourlyPicker from '../../components/orderer/newOrder/HourlyPicker';
import ContainerPicker from '../../components/orderer/newOrder/ContainerPicker';
import { choice } from '../../components/orderer/newOrder/choiceStyles';
import QuantityCalculator from '../../components/orderer/newOrder/QuantityCalculator';
import { useTruckCatalog } from '../../hooks/useTruckCatalog';
import { suggestTruck, calcLoads, loadsText, truckPayload, splitLoad, validateSplit } from '../../utils/truckSuggest';
import { hourlyTotal } from '../../utils/orderHourly';
import { getTruckSpec, supportsTrailer, isCraneCategory, BAG_TONS } from '../../utils/truckCatalog';
import { normalizeCraneLift } from '../../utils/craneLift';
import MassAccountCard from '../../components/orderer/newOrder/MassAccountCard';
import {
  defaultMassAccount, validateMassAccount, buildMassAccount, MASS_ACCOUNT_LIMITS,
} from '../../utils/massAccount';

const BILLING_MISSING_MESSAGE = 'Fyll ut fakturaopplysninger i profilen for å opprette oppdrag.';
const NO_PAYMENT_METHOD_MESSAGE = 'Kortbetaling er slått av, og kontoen din er ikke godkjent for faktura. Kontakt Tippi for å få fakturabetaling.';

const EMPTY_ADDR = { street: '', city: '', lat: null, lng: null };

const PAYER_OPTIONS = [
  { value: 'member', label: 'Meg selv' },
  { value: 'org',    label: 'Organisasjon' },
];

// Første valg i «Nytt oppdrag»: levering av masser (in) eller bortkjøring (out)
const FLOW_OPTIONS = [
  { value: 'in',  label: 'Levering av masser' },
  { value: 'out', label: 'Bortkjøring' },
];
const FLOW_HINTS = {
  in:  'Vi henter masser hos et utsalg og kjører dem til deg.',
  out: 'Vi henter masser hos deg og kjører dem til et mottak.',
};

// Enhetsvelgeren som står igjen når mengdefeltet er skjult (fulle lass)
const FULL_LOADS_UNITS = [
  { value: 'tons', label: 'Tonn' },
  { value: 'bags', label: 'Sekker' },
];
// Kroner med tusenskille, som prisforslaget
const fmtKr = n => Number(n).toLocaleString('nb-NO');

const TERMS_LOADING_MESSAGE = 'Vilkårene lastes, prøv igjen om et øyeblikk';
const REMOVAL_OPTION_LABEL = 'Hva skal kjøres bort?';
const REMOVAL_UNAVAILABLE = 'Bortkjøring er ikke tilgjengelig akkurat nå.';

export default function CreateOrder() {
  const massCatalog = useMassCatalog();
  const { currentUser, userDoc, refreshUserDoc } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { projects } = useOrdererProjects();
  const { locations: quickPickups, loading: pickupsLoading } = useQuickPickups();
  const [projectId, setProjectId] = useState(searchParams.get('projectId') ?? '');

  const [error, setError]     = useState('');
  const [page, setPage] = useState(0);
  const topRef = useRef(null);
  const firstPage = useRef(true);
  // Ny side: rull til toppen så bestilleren ser starten av siden
  useEffect(() => {
    if (firstPage.current) { firstPage.current = false; return; }
    const el = topRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
  }, [page]);
  const [loading, setLoading] = useState(false);
  const [showCardSetup, setShowCardSetup] = useState(false);
  const { terms, loaded: termsLoaded, error: termsError } = useTerms();
  // Kunne ikke vilkårene leses, brukes standardsatsene, og aksept kreves uansett tidligere godtatt versjon
  const needsAcceptance = termsError || (userDoc?.termsAcceptedVersion ?? 0) < terms.version;
  const [termsAccepted, setTermsAccepted] = useState(false);
  const publishAfterCard = useRef(false);

  const [pickup,           setPickup]           = useState(EMPTY_ADDR);
  const [delivery,         setDelivery]         = useState(EMPTY_ADDR);
  const [lastOrderAddress, setLastOrderAddress] = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    getDocs(
      query(collection(db, 'orders'),
        where('ordererUid', '==', currentUser.uid),
        orderBy('createdAt', 'desc'),
        limit(1)
      )
    ).then(snap => {
      if (!snap.empty) {
        const addr = snap.docs[0].data().pickupAddress;
        if (addr?.street) setLastOrderAddress(addr);
      }
    }).catch(() => {});
  }, [currentUser]);

  // Standard massetype: grus hvis aktiv, ellers første aktive (bortkjøring har egen vei og er aldri standard)
  const defaultMassType = () => {
    if (findMassType('gravel')?.active === true) return 'gravel';
    return activeMassTypes().find(t => t.key !== REMOVAL_MASS_TYPE)?.key ?? 'gravel';
  };
  const [massType,   setMassType]   = useState(defaultMassType);
  // Ingen vare er forhåndsvalgt: bestilleren må velge før neste seksjon vises. massType har bare en intern standard.
  const [massPicked, setMassPicked] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [massAccountTouched, setMassAccountTouched] = useState(false);
  const [massDesc,   setMassDesc]   = useState('');
  // Det bestiller skriver i mengdefeltet. Skjemaets mengde (tons) er avledet av denne lenger ned, siden fulle lass setter den selv.
  const [tonsInput, setTons] = useState('');
  const [unit,       setUnit]       = useState('tons');
  // Valget øverst på siden. Bortkjøring (out) lagres som removal: true.
  const [flow, setFlow] = useState('in');
  const removal = flow === 'out';
  const [needsEquip, setNeedsEquip] = useState(false);
  // Tippi foreslår lasteutstyr fra lasten. null = ikke endret av bestilleren, ellers bestillerens eget valg.
  const [equipmentOverride, setEquipmentOverride] = useState(null);
  const equipment = resolveEquipment({ override: equipmentOverride, massType, removal });
  const truckOverrides = useTruckCatalog();
  const [truckOverride, setTruckOverride] = useState(null);
  // Container (bare ved bortkjøring) krever krokløftbil. Størrelsen velges i ContainerPicker.
  const [container, setContainer] = useState({ on: false, size: null });
  const containerOn = removal && container.on && unit === 'tons';
  // Fulle lass: antall hele lass i stedet for en mengde. Bare for tonn, og ikke med container.
  const [fullLoads, setFullLoads] = useState({ on: false, count: 1 });
  const fullLoadsWanted = fullLoads.on && unit === 'tons' && !containerOn;
  // Timepris: bestiller betaler per time, og mengden er valgfri. Bare for tonn, ikke med container, og utelukker fulle lass.
  const [hourly, setHourly] = useState({ on: false, hours: 8 });
  const hourlyWanted = hourly.on && unit === 'tons' && !containerOn;
  const hourlyOn = hourlyWanted && !fullLoadsWanted;
  // Bilforslaget bruker ingen mengde i fulle lass (standard tippbil), bestiller velger bil selv.
  const suggestedTruck = suggestTruck({ unit, quantityTons: fullLoadsWanted ? '' : tonsInput }, truckOverrides);
  // Tyngste løft for kranbil: { tons, meters } som tekst, eller null
  const [craneLift, setCraneLift] = useState(null);
  const truck = containerOn ? { category: 'hooklift', axles: 3 } : (truckOverride ?? suggestedTruck);
  const truckSpec = getTruckSpec(truck.category, truck.axles, truckOverrides);
  // Veiledende timepris for valgt bil (null når ukjent)
  const truckHourlyRate = truckSpec?.hourlyRate > 0 ? truckSpec.hourlyRate : null;
  const trailerOn = truck.trailer === true && supportsTrailer(truck.category) && unit === 'tons';
  const truckLoadPayload = truckPayload({ ...truck, trailer: trailerOn }, truckOverrides);
  // Fulle lass gjelder bare med kjent nyttelast: uten den vises mengdefeltet i stedet for at et ukjent tall brukes i det skjulte.
  const fullLoadsOn = fullLoadsWanted && !!truckLoadPayload;
  // Fulle lass: mengden per ordre er hele nyttelasten (bil, pluss henger når valgt), rundet NED til 0,1 t så den aldri overstiger nyttelasten. Alt nedstrøms regnes per lass.
  const tons = fullLoadsOn ? String(Math.floor(truckLoadPayload * 10 + 1e-9) / 10) : tonsInput;
  const groupCount = fullLoadsOn ? fullLoads.count : 1;
  // Sekker regnes om til tonn (BAG_TONS per sekk) for lassberegningen
  const lassTons = unit === 'bags' ? Number(tons) * BAG_TONS : tons;
  const loadInfo = (unit === 'tons' || unit === 'bags') && truckLoadPayload ? calcLoads(lassTons, truckLoadPayload) : null;
  // Fordelingen mellom bil og henger. Bestillerens egen justering gjelder bare for mengden og bilen den ble satt for.
  const splitKey = `${tons}|${unit}|${truck.category}|${truck.axles}|${trailerOn}`;
  const [splitOverride, setSplitOverride] = useState(null);
  const customSplit = splitOverride?.key === splitKey ? splitOverride.value : null;
  // Endres mengde eller bil, er den gamle justeringen glemt for godt (den skal ikke komme tilbake om verdiene settes tilbake).
  useEffect(() => { setSplitOverride(null); }, [splitKey]);
  const trailerSpec = getTruckSpec('trailer', null, truckOverrides);
  const autoSplit = trailerOn && loadInfo && truckSpec ? splitLoad(loadInfo.tonsPerLoad, truckSpec.payloadTons, trailerSpec.payloadTons) : null;
  // Lagres og valideres i hele desimaler (0,1 t), slik at bil + henger alltid summerer til mengden per lass.
  const parseTons = (s) => Math.round(Number(String(s).trim().replace(',', '.')) * 10) / 10;
  const loadSplit = trailerOn && loadInfo
    ? (customSplit ? { truck: parseTons(customSplit.truck), trailer: parseTons(customSplit.trailer) } : autoSplit)
    : null;
  const splitProblem = trailerOn && loadInfo ? validateSplit(loadSplit, loadInfo.tonsPerLoad) : null;
  const containerProblem = containerOn && !container.size ? 'Velg containerstørrelse for å gå videre' : null;
  const truckProblem = splitProblem || containerProblem;
  const [massOption, setMassOption] = useState('');

  // Stedet (hurtigvalg eller samme adresse skrevet inn) som begrenser varene, null = ingen begrensning:
  // utsalget på hentested ved levering, mottaket på leveringssted ved bortkjøring.
  const supplier = useMemo(
    () => supplierAt(removal ? delivery : pickup, placesForFlow(quickPickups, flow)),
    [removal, flow, pickup, delivery, quickPickups],
  );

  // Utveiing og merking: kundenavn/kundenr hos stedet (utsalg ved levering, mottak ved bortkjøring) og merking av lasset.
  const massSupplierId = supplier?.id ?? null;
  const needsAccount = massSupplierId !== null;
  const typeDef = findMassType(massType, massCatalog);
  const optionList = typeDef ? activeOptions(typeDef) : [];
  // Undervalget som lagres: et kopiert valg som ikke lenger finnes for typen lagres ikke
  const savedOption = massOption && optionList.some(o => o.key === massOption) ? massOption : null;
  // Hva undervalget heter for bestilleren: egen etikett for bortkjøring, ellers typens etikett.
  const optionNoun = removal ? 'hva som skal kjøres bort' : (typeDef?.subLabel || 'undervalg').toLowerCase();
  const notOfferedMessage = (what) => (removal
    ? `${supplier.name} tar ikke imot ${what.toLowerCase()}. Velg annet mottak eller annen masse.`
    : `${supplier.name} leverer ikke ${what.toLowerCase()}. Velg annet hentested eller annen masse.`);
  // Valgt sted fører ikke den valgte varen: pris og tid vises ikke før bestilleren velger annet sted eller annen vare.
  const placeProblem = supplier && massPicked && typeDef?.active === true && !isOffered(supplier, massType, massOption || null, massCatalog)
    ? notOfferedMessage(massOption ? `${typeDef.label} ${optionLabel(massType, massOption, massCatalog)}` : typeDef.label)
    : null;
  const [massAccount, setMassAccount] = useState({ customerName: '', customerNumber: '', noAccount: false, reference: '' });
  const [showMassErrors, setShowMassErrors] = useState(false);
  const accountProblem = needsAccount && Object.keys(validateMassAccount(massAccount)).length > 0;
  const stages = orderStages({
    massPicked, massType, massOption, tons, requireOption: removal, quantityOptional: hourlyOn,
    pickupStreet: pickup.street, deliveryStreet: delivery.street,
    placeProblem, truckProblem, accountNeeded: needsAccount, accountProblem,
  }, massCatalog);
  // Hvilket sted massAccount-verdien gjelder for, slik at forhåndsutfylling ikke overskriver verdier (f.eks. fra kopi)
  const massAccountSupplierRef = useRef(null);
  // Kopi med eget massAccount: kopiens kundenr beholdes bare når det neste stedet er det samme som kopiens
  const keepCopiedMassAccount = useRef(false);
  const copiedMassSupplierId = useRef(null);
  // Satt når bestilleren (eller en kopi) har skrevet merkingen selv
  const referenceTouched = useRef(false);

  // Nytt sted: kundenr settes til husket verdi for stedet. Navn og merking beholdes (fylles bare hvis tomme).
  useEffect(() => {
    if (!massSupplierId) { massAccountSupplierRef.current = null; return; }
    if (massAccountSupplierRef.current === massSupplierId) return;
    massAccountSupplierRef.current = massSupplierId;
    if (keepCopiedMassAccount.current) {
      keepCopiedMassAccount.current = false;
      if (copiedMassSupplierId.current === massSupplierId) return;
    }
    const d = defaultMassAccount({
      billingProfile: userDoc?.billingProfile,
      project: projects.find(p => p.id === projectId),
      remembered: userDoc?.supplierAccounts,
      supplierId: massSupplierId,
    });
    setMassAccount(prev => ({
      customerName: prev.customerName || d.customerName,
      customerNumber: d.customerNumber,
      noAccount: false,
      reference: referenceTouched.current ? prev.reference : (prev.reference || d.reference),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [massSupplierId]);

  // Valgt prosjekt gir merking, så lenge bestilleren ikke har skrevet den selv
  useEffect(() => {
    if (referenceTouched.current) return;
    if (!projectId) {
      setMassAccount(prev => (prev.reference === '' ? prev : { ...prev, reference: '' }));
      return;
    }
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    const name = String(project.name ?? '').slice(0, MASS_ACCOUNT_LIMITS.reference);
    setMassAccount(prev => (prev.reference === name ? prev : { ...prev, reference: name }));
  }, [projectId, projects]);

  // Tidspunkt velges på hele/halve timer; standard er neste halvtime.
  const [scheduledAt,    setScheduledAt]    = useState(() => nextHalfHourLocal());
  const [priceMode,      setPriceMode]      = useState('fixed');
  const [fixedPrice,     setFixedPrice]     = useState('');
  const [priceEdited,    setPriceEdited]    = useState(false);
  const [autoAccept,     setAutoAccept]     = useState(true);
  const [allowBids,      setAllowBids]      = useState(false);
  const [bidDeadline,    setBidDeadline]    = useState(() => nextHalfHourLocal());

  // «Endre og publiser på nytt»: ?copy=<orderId> forhåndsutfyller fra bestillerens eget oppdrag
  // og viser seksjonene som er fylt ut. Det gamle (åpne) oppdraget lukkes når kopien er publisert.
  const copyId = searchParams.get('copy');
  const [copyLoading, setCopyLoading] = useState(!!copyId);
  const copySource = useRef(null);

  useEffect(() => {
    if (!copyId || !currentUser) return undefined;
    let cancelled = false;
    getDoc(doc(db, 'orders', copyId))
      .then(snap => {
        if (cancelled || !snap.exists()) return;
        const data = snap.data();
        if (data.ordererUid !== currentUser.uid) return;
        const p = buildCopyPrefill(data);
        setPickup(p.pickup);
        setDelivery(p.delivery);
        // Kopiens type/undervalg tas med som de er. Om de er deaktivert eller fjernet avgjøres ved hver visning
        // mot gjeldende katalog (orderStages, validateMass), så en katalog som lastes sent gir riktig resultat.
        setMassType(p.massType);
        setMassOption(p.massOption || '');
        setMassPicked(true);
        setMassDesc(p.massDesc);
        setTons(p.tons);
        setUnit(p.unit);
        setFlow(p.removal ? 'out' : 'in');
        setNeedsEquip(p.needsEquip);
        setEquipmentOverride(p.equipmentOverride);
        setTruckOverride(p.truckOverride);
        setContainer(p.containerSize ? { on: true, size: p.containerSize } : { on: false, size: null });
        setCraneLift(p.craneLift);
        setProjectId(p.projectId);
        // Timepris: samme timer og timepris. Timeprisen ligger i prisfeltet og regnes som bestillerens egen verdi.
        if (p.hourly) {
          setHourly({ on: true, hours: p.hourly.hours });
          setFixedPrice(String(p.hourly.rate));
          setPriceEdited(true);
        } else {
          setHourly({ on: false, hours: 8 });
        }
        if (p.massAccount) {
          const { supplierId: copiedSupplierId, ...copiedAccount } = p.massAccount;
          setMassAccount(copiedAccount);
          copiedMassSupplierId.current = copiedSupplierId;
          referenceTouched.current = true;
          keepCopiedMassAccount.current = true;
        }
        setPriceMode(p.priceMode);
        setScheduledAt(p.scheduledAt);
        setBidDeadline(p.bidDeadline);
        copySource.current = { id: snap.id ?? copyId, status: data.status };
      })
      .catch(err => console.error('CreateOrder: kunne ikke hente oppdrag å kopiere', err))
      .finally(() => { if (!cancelled) setCopyLoading(false); });
    return () => { cancelled = true; };
  }, [copyId, currentUser]);

  const { org } = useOrgMembership();
  const [payerType, setPayerType] = useState('member');

  // Sync from org.defaultPayer once org data loads
  useEffect(() => {
    if (!org) return;
    // If user has no personal stripeCustomerId, they must use the org payment
    if (org.stripeCustomerId && !userDoc?.stripeCustomerId) {
      setPayerType('org');
    } else if (org.defaultPayer) {
      setPayerType(org.defaultPayer);
    }
  }, [org?.defaultPayer, org?.stripeCustomerId, userDoc?.stripeCustomerId]);

  const distKm = pickup.lat && delivery.lat
    ? haversineKm(pickup.lat, pickup.lng, delivery.lat, delivery.lng)
    : null;

  // Systemets prisforslag (hele kroner) fra avstand, massetype og mengde.
  const suggestedPrice = useMemo(() => {
    if (distKm == null || !tons || !massType) return null;
    return calculateSuggestedPrice(distKm, massType, Number(tons), { removal });
  }, [distKm, tons, massType, removal]);

  // Prisfeltet følger forslaget til brukeren selv endrer det: avstandsprisen, eller veiledende timepris for bilen i timeoppdrag.
  const autoPrice = hourlyOn ? truckHourlyRate : suggestedPrice;
  useEffect(() => {
    setFixedPrice(current => nextAutoPrice({ edited: priceEdited, suggested: autoPrice, current }));
  }, [autoPrice, priceEdited, hourlyOn]);

  // Timepris som lagres: det bestiller har skrevet, ellers veiledende pris for bilen (anbud skjuler prisfeltet)
  const hourlyRateNum = Number(fixedPrice) > 0 ? Number(fixedPrice) : (truckHourlyRate ?? 0);

  function handleFixedPriceChange(value) {
    setFixedPrice(value);
    setPriceEdited(true);
  }
  function restoreSuggestedPrice() {
    setPriceEdited(false);
    setFixedPrice(nextAutoPrice({ edited: false, suggested: autoPrice, current: fixedPrice }));
  }

  // Prisfeltet gjelder enten timepris eller avstandspris. Skifter det, glemmes den gamle verdien og feltet følger forslaget på nytt.
  function resetPriceFollow() {
    setPriceEdited(false);
    setFixedPrice('');
  }
  // Timepris slås av (sekker, container eller fulle lass tar over).
  function dropHourly() {
    if (!hourly.on) return;
    setHourly(h => ({ ...h, on: false }));
    resetPriceFollow();
  }
  // Timepris og fulle lass utelukker hverandre: slås den ene på, slås den andre av.
  function changeHourly(next) {
    if (next.on !== hourly.on) resetPriceFollow();
    setHourly(next);
    if (next.on) setFullLoads(f => ({ ...f, on: false }));
  }
  function changeFullLoads(next) {
    setFullLoads(next);
    if (next.on) dropHourly();
  }

  // Container kan bare brukes med tonn: sekker må løftes av, så containeren nullstilles.
  function changeUnit(next) {
    setUnit(next);
    if (next === 'bags') {
      setContainer({ on: false, size: null });
      setFullLoads(f => ({ ...f, on: false }));
      dropHourly();
    }
  }

  // Container og fulle lass utelukker hverandre: slås container på, slås fulle lass av.
  // Nyttelasten per lass tas med som mengde, så skjemaet ikke tømmes under bestilleren.
  function changeContainer(next) {
    setContainer(next);
    if (next.on) {
      if (fullLoadsOn) setTons(tons);
      setFullLoads(f => ({ ...f, on: false }));
      dropHourly();
    }
  }

  // Bytte retning gjør de utfylte adressene og varen feil. Bortkjøring har sin egen vare (undervalg velges etterpå),
  // levering starter uten valgt vare.
  function changeFlow(next) {
    if (next === flow) return;
    setFlow(next);
    setPickup(EMPTY_ADDR);
    setDelivery(EMPTY_ADDR);
    setMassOption('');
    if (next === 'in') setContainer({ on: false, size: null });
    if (next === 'out') {
      setMassType(REMOVAL_MASS_TYPE);
      setMassPicked(true);
    } else {
      setMassType(defaultMassType());
      setMassPicked(false);
    }
    setError('');
  }

  function selectMass(key) {
    setMassPicked(true);
    if (key === massType && massPicked) return; // allerede valgt: behold undervalget
    setMassType(key);
    setMassOption('');
  }

  function validatePlaces() {
    if (!pickup.street.trim())   return 'Hente-adresse er påkrevd';
    if (!delivery.street.trim()) return 'Leveringsadresse er påkrevd';
    return null;
  }
  function validateMass() {
    // Timeoppdrag: mengden er valgfri, men er den fylt ut må den være over 0
    const qtyEmpty = String(tons ?? '').trim() === '';
    if (hourlyOn ? (!qtyEmpty && !(Number(tons) > 0)) : (!tons || Number(tons) <= 0)) return 'Angi mengde';
    const typeDef = findMassType(massType);
    if (typeDef?.active !== true) return removal ? REMOVAL_UNAVAILABLE : 'Velg massetype';
    if (supplier && !isOffered(supplier, massType, null, massCatalog)) return notOfferedMessage(typeDef.label);
    const subProblem = `Velg ${optionNoun}`;
    const typeOptions = activeOptions(typeDef);
    if ((typeDef.subRequired || removal) && typeOptions.length > 0 && !massOption) return subProblem;
    if (typeOptions.length > 0 && massOption && !typeOptions.some(o => o.key === massOption)) return subProblem;
    if (supplier && massOption && !isOffered(supplier, massType, massOption, massCatalog)) {
      return notOfferedMessage(`${typeDef.label} ${optionLabel(massType, massOption, massCatalog)}`);
    }
    if (needsAccount) {
      const errs = validateMassAccount(massAccount);
      if (Object.keys(errs).length) { setShowMassErrors(true); return Object.values(errs)[0]; }
    }
    return null;
  }
  // Seksjonene er låst bak hverandre, men katalogen kan ha endret seg etter at skjemaet ble fylt ut: valider alt før publisering.
  function validateBeforePublish() {
    return validateMass() || validatePlaces() || validatePrice();
  }
  // Går en tidligere side ugyldig (f.eks. katalogen endret seg), sendes bestilleren til første ugyldige side.
  function guardPage() {
    const bad = PAGES.findIndex((_, i) => i < PAGES.length - 1 && !pageComplete(stages, i));
    if (bad === -1) return false;
    setPage(bad);
    return true;
  }
  function goNext() {
    if (page === 3) {
      const problem = validatePriceStep();
      if (problem) { setError(problem); return; }
    }
    setError('');
    setPage(p => Math.min(p + 1, PAGES.length - 1));
  }
  function goBack() {
    setError('');
    setPage(p => Math.max(p - 1, 0));
  }
  function validatePriceStep() {
    const timeProblem = validateHalfHour(scheduledAt);
    if (timeProblem === 'empty') return 'Velg ønsket leveringstidspunkt';
    if (timeProblem === 'invalid') return 'Leveringstidspunktet må være på hel eller halv time';
    if (timeProblem === 'past') return 'Leveringstidspunktet kan ikke være i fortiden';
    if (priceMode === 'fixed' && (!fixedPrice || Number(fixedPrice) <= 0)) return hourlyOn ? 'Angi timepris' : 'Angi fastpris';
    if (hourlyOn && !(hourlyRateNum > 0)) return 'Angi timepris';
    if (hourlyOn && !Number.isInteger(hourlyRateNum)) return 'Timepris må være hele kroner';
    // I timeoppdrag er det totalen (timer × timepris) som lagres som fastpris
    const fixedTotal = hourlyOn ? hourlyTotal(hourly.hours, hourlyRateNum) : Number(fixedPrice);
    if (priceMode === 'fixed' && autoAccept && finalPayerType !== 'org' && !Number.isInteger(fixedTotal)) {
      return 'Fastprisen må være et helt kronebeløp når oppdraget godtas automatisk';
    }
    if (priceMode === 'bid') {
      const deadlineProblem = validateHalfHour(bidDeadline);
      if (deadlineProblem === 'empty') return 'Angi anbudsfrist';
      if (deadlineProblem === 'invalid') return 'Anbudsfristen må være på hel eller halv time';
      if (deadlineProblem === 'past') return 'Anbudsfristen kan ikke være i fortiden';
    }
    return null;
  }
  function validateTerms() {
    if (!termsLoaded) return TERMS_LOADING_MESSAGE;
    if (needsAcceptance && !termsAccepted) return TERMS_REQUIRED_MESSAGE;
    return null;
  }
  function validatePrice() {
    return validatePriceStep() || validateTerms();
  }

  // Betaleren som faktisk lagres på oppdraget (org-betaling bare når org har Stripe-kunde).
  const effectivePayerType = (userDoc?.orgId && org?.stripeCustomerId) ? payerType : null;

  // Betalingsmåte: faktura krever godkjent fakturakunde (org foretrekkes for medlemmer).
  const invoiceFeature = useInvoiceFeature();
  const { enabled: cardEnabled, loading: cardLoading } = useCardFeature();
  const { personal, org: orgTerms } = useMyInvoiceTerms({ uid: currentUser?.uid, orgId: userDoc?.orgId });
  const invoicePayer = invoiceFeature.enabled ? resolveInvoicePayer({ userDoc, personal, org: orgTerms }) : null;
  const [paymentMethod, setPaymentMethod] = useState('card');
  // Er kort av, er faktura eneste mulighet (krever godkjent fakturakunde).
  const isInvoice = (paymentMethod === 'invoice' || !cardEnabled) && invoicePayer != null;
  const finalPayerType = isInvoice ? invoicePayer.payerType : effectivePayerType;
  const cardRequired = cardEnabled && !isInvoice && needsCardToPublish({ userDoc, payerType: effectivePayerType });
  const noPaymentMethod = !cardEnabled && !isInvoice;
  // Fakturaopplysninger kreves i tillegg til kortkravet; reglene avviser også oppdrag uten dem.
  const billingMissing = !isBillingProfileComplete(userDoc?.billingProfile);

  useEffect(() => {
    if (finalPayerType === 'org') { setAutoAccept(false); setAllowBids(false); }
  }, [finalPayerType]);

  async function handleSubmit() {
    if (cardLoading) return; // kortbryteren er ikke lest ennå — unngå feil betalingsvei
    if (billingMissing) { setError(BILLING_MISSING_MESSAGE); return; }
    if (noPaymentMethod) { setError(NO_PAYMENT_METHOD_MESSAGE); return; }
    if (guardPage()) return;
    const err = validateBeforePublish();
    if (err) {
      if (validatePriceStep()) setPage(3);
      setError(err);
      return;
    }
    setError('');
    if (cardRequired) {
      publishAfterCard.current = true;
      setShowCardSetup(true);
      return;
    }
    await publishOrder();
  }

  // Kortet er lagret — oppdater brukeren og, hvis brukeren hadde forsøkt å publisere, publiser det som er fylt ut.
  async function handleCardSaved() {
    await refreshUserDoc();
    setShowCardSetup(false);
    if (!publishAfterCard.current) return;
    publishAfterCard.current = false;
    if (guardPage()) return;
    const err = validateBeforePublish();
    if (err) {
      if (validatePriceStep()) setPage(3);
      setError(err);
      return;
    }
    await publishOrder();
  }

  // Vakt mot dobbel publisering (to raske klikk eller kort-lagring og klikk samtidig): ellers lages 2N ordrer.
  const publishing = useRef(false);

  async function publishOrder() {
    if (billingMissing) { setError(BILLING_MISSING_MESSAGE); return; }
    if (noPaymentMethod) { setError(NO_PAYMENT_METHOD_MESSAGE); return; }
    if (publishing.current) return;
    publishing.current = true;
    setLoading(true);
    setError('');
    try {
      const batch = writeBatch(db);

      // Ordre-ID-ene finnes før dokumentene lages, så bildene kan lastes opp først (én gang, delt av alle ordrene)
      const refs = Array.from({ length: groupCount }, () => doc(collection(db, 'orders')));
      const groupId = groupCount > 1 ? refs[0].id : null;
      let ordererPhotoUrls = [];
      if (photos.length > 0) {
        try {
          ordererPhotoUrls = await uploadPhotos(photos, `orderPhotos/${currentUser.uid}/${refs[0].id}`);
        } catch (uploadErr) {
          console.error('CreateOrder: kunne ikke laste opp bilder', uploadErr);
          setError('Kunne ikke laste opp bildene. Prøv igjen, eller fjern bildene.');
          return;
        }
      }

      const orderData = {
        ordererUid:               currentUser.uid,
        projectId:                projectId || null,
        status:                   'open',
        pickupAddress:            pickup,
        pickupSupplierId:         removal ? null : (supplier?.id ?? null),
        deliverySupplierId:       removal ? (supplier?.id ?? null) : null,
        deliveryAddress:          delivery,
        massType,
        massDescription:          massDesc.trim(),
        // Timeoppdrag kan mangle mengde
        estimatedTons:            String(tons ?? '').trim() === '' ? null : Number(tons),
        unit,
        removal,
        requiresLoadingEquipment: needsEquip,
        loadingEquipment:         needsEquip ? equipment : [],
        // Bilkravet lagres når bestiller har valgt bil selv, ved sekker (kran kreves) og ved henger (sjåføren må ha henger).
        // Ellers er forslaget kun veiledning.
        truckCategory:            containerOn ? 'hooklift' : (truckOverride || unit === 'bags' || trailerOn ? truck.category : null),
        truckAxlesMin:            containerOn ? null : (truckOverride || trailerOn ? (truck.axles ?? null) : null),
        containerSize:            containerOn ? container.size : null,
        craneLift:                !containerOn && isCraneCategory(truck.category) ? normalizeCraneLift(craneLift) : null,
        truckTrailer:             trailerOn,
        loadSplit:                trailerOn && loadSplit ? { truck: loadSplit.truck, trailer: loadSplit.trailer } : null,
        loadCount:                loadInfo ? loadInfo.loads : null,
        tonsPerLoad:              loadInfo ? loadInfo.tonsPerLoad : null,
        massAccount:              needsAccount ? buildMassAccount(massAccount, massSupplierId) : null,
        ordererPhotoUrls,
        massOption:               savedOption,
        // Gamle felt beholdes så eldre klienter fortsatt viser undervalget
        crushedStoneGrade:        massType === 'crushed_stone' ? savedOption : null,
        soilQuality:              massType === 'soil'          ? savedOption : null,
        scheduledAt:              Timestamp.fromDate(new Date(scheduledAt)),
        bidDeadline:              priceMode === 'bid'
                                    ? Timestamp.fromDate(new Date(bidDeadline))
                                    : null,
        hourly:                   hourlyOn ? { hours: hourly.hours, rate: hourlyRateNum } : null,
        fixedPrice:               priceMode === 'fixed' ? (hourlyOn ? hourlyTotal(hourly.hours, hourlyRateNum) : Number(fixedPrice)) : null,
        autoAccept:               priceMode === 'fixed' && finalPayerType !== 'org' && autoAccept,
        allowBids:                priceMode === 'fixed' && finalPayerType !== 'org' && autoAccept && allowBids,
        suggestedPrice:           hourlyOn ? 0 : (suggestedPrice ?? 0), // timeoppdrag har ingen avstandsbasert anbefaling
        acceptedBidId:            null,
        driverUid:                null,
        createdAt:                serverTimestamp(),
        orgId:                    userDoc?.orgId ?? null,
        payerType:                finalPayerType,
        paymentMethod:            isInvoice ? 'invoice' : 'card',
        // Kopiert fra bestillerens users.isTestAccount ved opprettelse — immutable
        // (håndhevet av firestore.rules), styrer hvilken Stripe-modus ordren kjører i.
        isTestAccount:            userDoc?.isTestAccount ?? false,
        termsVersion:             terms.version,
      };
      // Flere fulle lass: én ordre per lass, knyttet sammen av groupId
      refs.forEach((orderRef, i) => {
        batch.set(orderRef, {
          ...orderData,
          groupId,
          groupIndex: groupCount > 1 ? i + 1 : null,
          groupSize:  groupCount > 1 ? groupCount : null,
        });
      });

      // Inkrementer orderCount på publicProfiles atomisk
      const profileRef = doc(db, 'publicProfiles', currentUser.uid);
      batch.update(profileRef, { orderCount: increment(groupCount) });

      await batch.commit();

      // Husk kundenr per utsalg til neste gang. Best-effort: feil skal ikke hindre navigering.
      if (needsAccount && !massAccount.noAccount && massAccount.customerNumber.trim()) {
        // Kjører uten å blokkere navigeringen; brukerdokumentet oppdateres når lagringen er ferdig.
        try {
          Promise.resolve(setDoc(doc(db, 'users', currentUser.uid),
            { supplierAccounts: { [massSupplierId]: massAccount.customerNumber.trim().slice(0, MASS_ACCOUNT_LIMITS.customerNumber) } },
            { merge: true }))
            .then(() => refreshUserDoc())
            .catch(e => console.warn('CreateOrder: kunne ikke huske kundenr', e));
        } catch (e) {
          console.warn('CreateOrder: kunne ikke huske kundenr', e);
        }
      }

      // Husk godtatte vilkår. Best-effort: feil skal ikke hindre navigering.
      if (needsAcceptance && termsAccepted) {
        try {
          Promise.resolve(setDoc(doc(db, 'users', currentUser.uid),
            { termsAcceptedVersion: terms.version, termsAcceptedAt: serverTimestamp() },
            { merge: true }))
            .then(() => refreshUserDoc())
            .catch(e => console.warn('CreateOrder: kunne ikke lagre godtatte vilkår', e));
        } catch (e) {
          console.warn('CreateOrder: kunne ikke lagre godtatte vilkår', e);
        }
      }

      // Kopi av et åpent oppdrag: lukk det gamle. Feil her skal ikke hindre navigering til det nye.
      const old = copySource.current;
      if (old?.status === 'open') {
        try {
          await stripeApi.cancelOrderByOrderer(old.id);
        } catch (cancelErr) {
          console.error('CreateOrder: kunne ikke lukke det gamle oppdraget', cancelErr);
        }
      }
      navigate(groupCount > 1 ? '/orderer' : `/orderer/order/${refs[0].id}`);
    } catch (err) {
      console.error(err);
      setError('Kunne ikke opprette oppdrag. Prøv igjen.');
    } finally {
      publishing.current = false;
      setLoading(false);
    }
  }

  const projectChips = projects
    .filter(p => p.address?.street)
    .map(p => ({ key: p.id, name: p.name, street: p.address.street, city: p.address.city, lat: p.address.lat, lng: p.address.lng, sub: p.address.street }));
  const projectStreets = new Set(projectChips.map(c => c.street));
  const showLastOrder = lastOrderAddress && !projectStreets.has(lastOrderAddress.street);
  const deliveryChips = [
    ...projectChips,
    ...(showLastOrder ? [{ key: '__last__', name: 'Siste oppdrag', ...lastOrderAddress, sub: lastOrderAddress.street }] : []),
  ];
  const toChip = loc => ({ key: loc.id, name: loc.name, sub: loc.subtitle, street: loc.street, city: loc.city, lat: loc.lat, lng: loc.lng, logoUrl: loc.logoUrl });
  // Hurtigvalg viser bare steder som fører valgt vare
  const supplierChips = offeredPlaces(placesForFlow(quickPickups, 'in'), massType, massOption, massCatalog).map(toChip);
  const receiverChips = offeredPlaces(placesForFlow(quickPickups, 'out'), massType, massOption, massCatalog).map(toChip);
  const knownPlaceChips = quickPickups.map(toChip);

  const pin = <NewOrderIcon name="pin" size={19} />;
  // Forslagene som vises når adressefeltet får fokus. Gruppene fra kjente steder venter til stedene er lastet.
  const places = !pickupsLoading;
  const pickupSuggestions = removal
    ? [{ title: 'Mine adresser', chips: deliveryChips }]
    : [{ title: 'Kjente masseleverandører', chips: places ? supplierChips : [] }];
  const deliverySuggestions = removal
    ? [{ title: 'Kjente mottak', chips: places ? receiverChips : [] }]
    : [{ title: 'Mine adresser', chips: deliveryChips }, { title: 'Kjente steder', chips: places ? knownPlaceChips : [] }];

  // Bortkjøring uten aktiv type i katalogen: hintet sier det samme som meldingen på siden
  const nextHint = removal && typeDef?.active !== true ? REMOVAL_UNAVAILABLE : {
    product: 'Velg produkt for å gå videre',
    option: `Velg ${optionNoun} for å gå videre`,
    quantity: 'Angi mengde for å gå videre',
    truck: truckProblem,
    places: placeProblem ? 'Velg annet sted eller annen vare for å gå videre' : 'Velg hente- og leveringssted for å gå videre',
    account: 'Fyll ut kundenavn og kundenr for å gå videre',
  }[stages.next] ?? '';

  const lastPage = page === PAGES.length - 1;
  const backButton = page > 0 ? (
    <button type="button" className="tippi-btn" aria-label="Tilbake" style={ctaStyles.back} onClick={goBack}>
      <NewOrderIcon name="arrowLeft" size={20} />
    </button>
  ) : null;

  const publishFooter = (
    <div key="publish" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {billingMissing && <BillingProfileNotice>{BILLING_MISSING_MESSAGE}</BillingProfileNotice>}
      <button
        type="button"
        className="tippi-btn"
        style={loading ? { ...ctaStyles.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 } : ctaStyles.primary}
        disabled={loading || billingMissing}
        // Publiser ligger der Neste var: andre klikk i et dobbeltklikk på Neste skal ikke publisere
        onClick={e => { if (e.detail > 1) return; handleSubmit(); }}
      >
        {loading && <TruckLoader size={24} decorative />}
        {loading ? 'Publiserer…' : 'Publiser oppdrag'}
      </button>
    </div>
  );

  let mainFooter;
  if (lastPage) {
    mainFooter = stages.canPublish ? publishFooter : <div style={ctaStyles.hint}>{nextHint}</div>;
  } else if (pageComplete(stages, page)) {
    mainFooter = <button key="next" type="button" className="tippi-btn" style={ctaStyles.primary} onClick={goNext}>Neste</button>;
  } else {
    mainFooter = <div style={ctaStyles.hint}>{nextHint}</div>;
  }
  const footer = <>{backButton}{mainFooter}</>;

  if (copyLoading) {
    return (
      <div className="orderer-ui" style={styles.root}>
        <Navbar />
        <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
          <TruckLoader size={48} label="Henter oppdraget…" />
        </div>
      </div>
    );
  }

  return (
    <div className="orderer-ui" style={styles.root}>
      <Navbar />
      <NewOrderShell
        onClose={() => navigate('/orderer')}
        error={error}
        footer={footer}
        step={{ current: page + 1, total: PAGES.length, title: PAGES[page].title }}
      >
        <span ref={topRef} aria-hidden="true" style={{ display: 'block', height: 0, marginTop: -10 }} />

        {page === 0 && (
          <>
            {/* ── Retning ─────────────────────────────────────── */}
            <RevealSection label="Type oppdrag">
              <SegmentedControl ariaLabel="Hva trenger du?" fill options={FLOW_OPTIONS} value={flow} onChange={changeFlow} />
              <div style={styles.hintText}>{FLOW_HINTS[flow]}</div>
            </RevealSection>

            {/* ── Produkt (bare ved levering) ─────────────────── */}
            {!removal && (
              <RevealSection label="Produkt">
                <MassTiles value={massPicked ? massType : null} onChange={selectMass} excludeKeys={[REMOVAL_MASS_TYPE]} />
                {stages.complete.product && <MassHint text={typeHint(massType)} />}
              </RevealSection>
            )}

            {/* ── Underkategori ───────────────────────────────── */}
            {!removal && stages.visible.option && (
              <RevealSection label={`Valg av ${optionNoun}`}>
                <OptionSelect
                  id="new-order-option"
                  label={typeDef?.subLabel || 'Velg'}
                  required={typeDef?.subRequired}
                  options={optionList.map(o => ({ value: o.key, label: o.label }))}
                  value={massOption}
                  onChange={setMassOption}
                />
                {massOption && <MassHint text={optionHint(massType, massOption)} />}
              </RevealSection>
            )}

            {/* ── Bortkjøring: bare hva som skal kjøres bort ──── */}
            {removal && (typeDef?.active === true ? (
              <RevealSection label="Hva skal kjøres bort">
                <OptionSelect
                  id="new-order-option"
                  label={REMOVAL_OPTION_LABEL}
                  required
                  options={optionList.map(o => ({ value: o.key, label: o.label }))}
                  value={massOption}
                  onChange={setMassOption}
                />
                {massOption && <MassHint text={optionHint(massType, massOption)} />}
              </RevealSection>
            ) : (
              <div role="status" style={styles.problemText}>{REMOVAL_UNAVAILABLE}</div>
            ))}
          </>
        )}

        {page === 1 && (
          <>
            {/* ── Mengde ──────────────────────────────────────── */}
            {stages.visible.quantity && (
              <RevealSection label="Mengde og enhet">
                {unit === 'tons' && !containerOn && (
                  <FullLoadsPicker value={fullLoads} onChange={changeFullLoads} payloadTons={truckLoadPayload} max={MAX_FULL_LOADS} />
                )}
                {unit === 'tons' && !containerOn && (
                  <HourlyPicker value={hourly} onChange={changeHourly} />
                )}
                {fullLoadsOn ? (
                  // Mengdefeltet er skjult i fulle lass, men enheten kan fortsatt byttes (til sekker)
                  <SegmentedControl ariaLabel="Enhet" options={FULL_LOADS_UNITS} value={unit} onChange={changeUnit} />
                ) : (
                  <QuantityField
                    value={tonsInput} onChange={setTons} unit={unit} onUnitChange={changeUnit}
                    label={hourlyOn ? 'Omtrentlig mengde (valgfri)' : 'Mengde'}
                  />
                )}
                {unit === 'tons' && !fullLoadsOn && <QuantityCalculator massType={massType} onUse={setTons} />}
              </RevealSection>
            )}

            {/* ── Bil og lasteutstyr ──────────────────────────── */}
            {stages.visible.truck && (
              <RevealSection label="Bil og lasteutstyr">
                {removal && unit === 'tons' && <ContainerPicker value={container} onChange={changeContainer} />}
                {containerOn ? (
                  <div style={choice.card}>
                    <div style={choice.title}>Bil</div>
                    <p style={choice.sum}>Krever krokløftbil</p>
                    {loadsText(loadInfo, unit) && <div style={choice.hint}>Mengden blir {loadsText(loadInfo, unit)} med denne bilen.</div>}
                  </div>
                ) : (
                  <TruckPicker
                    value={truck}
                    suggested={suggestedTruck}
                    onChange={setTruckOverride}
                    overrides={truckOverrides}
                    unit={unit}
                    quantityTons={tons}
                    split={customSplit}
                    onSplitChange={value => setSplitOverride({ key: splitKey, value })}
                    splitProblem={splitProblem}
                    craneLift={craneLift}
                    onCraneLiftChange={setCraneLift}
                    fullLoads={fullLoadsOn}
                  />
                )}

                <div style={{ ...choice.card, padding: '4px 14px 12px' }}>
                  <SwitchRow
                    title="Krever lasteutstyr" sub="Gravemaskin, hjullaster eller lignende"
                    checked={needsEquip} onChange={setNeedsEquip}
                  />
                  {needsEquip && (
                    <>
                      <div style={choice.divider} />
                      <EquipmentChips value={equipment} onToggle={v => setEquipmentOverride(toggleEquipment(equipment, v))} />
                    </>
                  )}
                </div>
              </RevealSection>
            )}
          </>
        )}

        {/* ── Steder ──────────────────────────────────────── */}
        {page === 2 && stages.visible.places && (
          <RevealSection label="Steder">
            <AddressInput
              variant="field" icon={pin} label="Hentested" value={pickup} onChange={setPickup}
              required allowMyLocation allowPin={flow === 'out'} suggestions={pickupSuggestions}
            />
            <AddressInput
              variant="field" icon={pin} label="Leveringssted" value={delivery} onChange={setDelivery}
              required allowMyLocation allowPin={flow === 'in'} suggestions={deliverySuggestions}
            />

            {placeProblem && <div role="status" style={styles.problemText}>{placeProblem}</div>}

            {distKm != null && <RouteCard pickup={pickup} delivery={delivery} distKm={distKm} />}
            {distKm != null && (
              <div style={styles.hintText}>Luftlinjeavstand — faktisk kjørelengde kan avvike.</div>
            )}
          </RevealSection>
        )}

        {/* ── Utveiing og merking ─────────────────────────── */}
        {page === 3 && stages.visible.account && (
          <RevealSection label="Kunde hos stedet">
            <MassAccountCard
              value={massAccount}
              onChange={v => {
                if (v.reference !== massAccount.reference) referenceTouched.current = true;
                setMassAccountTouched(true);
                setMassAccount(v);
              }}
              errors={validateMassAccount(massAccount)}
              showErrors={showMassErrors || massAccountTouched}
              removal={removal}
              supplierName={supplier.name}
            />
          </RevealSection>
        )}

        {/* ── Pris og tid ─────────────────────────────────── */}
        {page === 3 && stages.visible.price && (
          <RevealSection label="Pris og tid">
            <PriceStep
              scheduledAt={scheduledAt} onScheduledAtChange={setScheduledAt}
              priceMode={priceMode} onPriceModeChange={setPriceMode}
              fixedPrice={fixedPrice} onFixedPriceChange={handleFixedPriceChange}
              hourly={hourlyOn ? { hours: hourly.hours } : null}
              priceHint={hourlyOn ? null : buildPriceHint({ suggested: suggestedPrice, tons, unit, distKm })}
              canRestorePrice={priceEdited && autoPrice > 0 && String(Math.round(autoPrice)) !== String(fixedPrice)}
              onRestorePrice={restoreSuggestedPrice}
              autoAccept={autoAccept} onAutoAcceptChange={setAutoAccept}
              allowBids={allowBids} onAllowBidsChange={setAllowBids}
              canAutoAccept={finalPayerType !== 'org'}
              bidDeadline={bidDeadline} onBidDeadlineChange={setBidDeadline}
            />
            {groupCount > 1 && (
              <div style={styles.hintText}>
                <div>{priceMode === 'bid' ? 'Budfristen og prisen gjelder hvert lass.' : 'Prisen gjelder per lass.'}</div>
                {priceMode === 'fixed' && Number(fixedPrice) > 0 && (
                  <div>{`${groupCount} lass: ${groupCount} × ${fmtKr(fixedPrice)} kr = ${fmtKr(groupCount * Number(fixedPrice))} kr`}</div>
                )}
              </div>
            )}
          </RevealSection>
        )}

        {/* ── Resten: prosjekt, beskrivelse, bilder, betaling, vilkår ─ */}
        {page === 4 && stages.visible.extras && (
          <RevealSection label="Tillegg og betaling">
            {projects.length > 0 && (
              <IconField label="Prosjekt (valgfritt)" icon="layers" htmlFor="new-order-project">
                <select id="new-order-project" style={{ ...bareInputStyle, appearance: 'none' }} value={projectId} onChange={e => setProjectId(e.target.value)}>
                  <option value="">Ingen prosjekt</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <NewOrderIcon name="chevronDown" size={18} />
              </IconField>
            )}

            <IconField label="Beskrivelse (valgfritt)" htmlFor="new-order-desc">
              <input
                id="new-order-desc"
                style={{ ...bareInputStyle, fontWeight: 500 }}
                placeholder="F.eks. adkomst, port eller kontaktperson"
                value={massDesc}
                onChange={e => setMassDesc(e.target.value)}
                maxLength={200}
              />
            </IconField>

            <PhotoPicker files={photos} onChange={setPhotos} />

            {/* ── Betales av / med ──────────────────────────────────────── */}
            {invoicePayer && (
              <PaymentMethodPicker value={paymentMethod} onChange={setPaymentMethod} customer={invoicePayer.customer} cardEnabled={cardEnabled} />
            )}

            {cardEnabled && !isInvoice && userDoc?.orgId && org?.stripeCustomerId && (
              userDoc?.stripeCustomerId ? (
                <div style={styles.fieldGroup}>
                  <span style={fieldLabelStyle}>Betales av</span>
                  <SegmentedControl options={PAYER_OPTIONS} value={payerType} onChange={setPayerType} ariaLabel="Betales av" fill />
                  <div style={styles.hintText}>
                    {payerType === 'org' ? `Bedriftskort — ${org.orgName}` : 'Personlig betaling med ditt eget kort'}
                  </div>
                </div>
              ) : (
                <div style={styles.fieldGroup}>
                  <span style={fieldLabelStyle}>Betales av</span>
                  <div style={styles.orgOnly}>
                    <strong>{org.orgName}</strong>
                    <span style={{ color: COLORS.textMid }}> · Bedriftskort — ingen annen betalingsmåte tilgjengelig</span>
                  </div>
                </div>
              )
            )}

            <TermsNotice
              terms={terms}
              needsAcceptance={needsAcceptance}
              accepted={termsAccepted}
              onChange={setTermsAccepted}
              showError={error === TERMS_REQUIRED_MESSAGE}
            />

            {cardRequired && (
              <CardPrompt
                open={showCardSetup}
                onAdd={() => { publishAfterCard.current = false; setShowCardSetup(true); }}
              >
                <PaymentSetup onSuccess={handleCardSaved} />
              </CardPrompt>
            )}
          </RevealSection>
        )}
      </NewOrderShell>
    </div>
  );
}

const styles = {
  root: { minHeight: '100vh', background: COLORS.bg, display: 'flex', flexDirection: 'column' },
  fieldGroup: { display: 'flex', flexDirection: 'column' },
  hintText: { fontSize: 12, color: COLORS.textMid, marginTop: 6, lineHeight: 1.4 },
  problemText: { fontSize: 13, color: COLORS.dangerText, background: COLORS.dangerBg, borderRadius: 8, padding: '9px 12px', lineHeight: 1.4 },
  orgOnly: { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: '12px 14px', fontSize: 13.5, color: COLORS.textDark },
};
